// v3.15-921: Send-to-Agent modal — purpose-built lightweight email composer
// opened from each offer card's "Send to Agent" button. Pre-fills subject and
// body from the offer values; user can edit any field before sending. CC is
// pre-filled with a.davis@prestigioustravelclub.org per spec. Hits
// POST /api/email/send with activity_type="agent_offer_sent" so the row in
// the client's timeline is tagged correctly.
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import TemplateSelector from './TemplateSelector';
import { TemplateLoadedBanner, confirmReplaceDraft } from './templateSelectorHelpers';
import SaveAsTemplateDialog from './SaveAsTemplateDialog';
import { useAuth } from '../contexts/AuthContext';

// v3.15-1086: Removed PERMANENT_CC. The hardcoded `a.davis@prestigioustravelclub.org`
// auto-CC violated the universal email policy ("no hidden recipients").
// CC now defaults to the assigned deal-team (Director / Manager / Sales Rep /
// Concierge / Application Manager / VO Officer) via
// `GET /api/clients/{id}/default-recipients?context=external`. Operator
// retains full edit control on the CC field.

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : `$${n.toLocaleString()}`;
};
const fmtNum = (v, suffix = '') => {
  if (v === null || v === undefined || v === '') return '-';
  return `${v}${suffix}`;
};

export default function SendOfferToAgentModal({ open, onOpenChange, client, offer, offerNumber }) {
  const { isAdmin } = useAuth();
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [to, setTo]       = useState('');
  const [cc, setCc]       = useState('');
  const [subject, setSubj] = useState('');
  const [body, setBody]   = useState('');
  const [sending, setSending] = useState(false);
  // v3.15-1088 — template selector state.
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  const [baselineDraft, setBaselineDraft] = useState({ subject: '', body: '' });

  useEffect(() => {
    if (!open || !offer) return;
    const clientName  = client?.full_name || '';
    const clientPhone = client?.phone || '';
    const dealAmt     = fmtMoney(offer.deal_amount ?? offer.applied_for);
    const apprAmt     = fmtMoney(offer.approved_amount);
    const term        = fmtNum(offer.term, ' mo');
    const payment     = fmtMoney(offer.payment) + (offer.payment ? '/mo' : '');
    const rate        = fmtNum(offer.rate, '%');

    setTo('');
    setCc('');  // overridden below by /default-recipients fetch (v3.15-1086)
    setSubj(`Offer Details for ${clientName || 'Client'}`);
    setBody(
      `Client Name: ${clientName || '-'}\n` +
      `Phone: ${clientPhone || '-'}\n\n` +
      `Deal Amount: ${dealAmt}\n` +
      `Approved Amount: ${apprAmt}\n` +
      `Term: ${term}\n` +
      `Payment: ${payment}\n` +
      `Rate: ${rate}\n\n` +
      `Please review and advise on next steps.`
    );

    // v3.15-1086: hydrate default CC with the assigned deal-team only —
    // no hardcoded permanent CC, no company-wide expansion.
    if (client?.id) {
      api.get(`/clients/${client.id}/default-recipients`, { params: { context: 'external' } })
        .then(({ data }) => {
          const team = (data?.cc || []).filter(Boolean);
          if (team.length) setCc(team.join(', '));
        })
        .catch(() => { /* leave CC blank — operator can add manually */ });
    }
  }, [open, offer, client?.id, client?.full_name, client?.phone, offerNumber]);

  const send = async () => {
    if (!to.trim()) { toast.error('"To" address is required'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body cannot be empty'); return; }
    setSending(true);
    try {
      const payload = {
        client_id: client?.id,
        to_email: to.trim(),
        cc: cc.trim() || null,
        subject: subject.trim(),
        message: body,
        template: 'custom',
        activity_type: 'agent_offer_sent',
        // v3.15-1088 — stamp v2 template usage for activity logging.
        template_id:       loadedTemplate?.id || null,
        template_name:     loadedTemplate?.name || null,
        template_key:      loadedTemplate?.key || null,
        template_category: loadedTemplate?.category || null,
        activity_metadata: {
          offer_number: offerNumber,
          recipient: to.trim(),
          source: 'send_offer_to_agent',
        },
      };
      await api.post('/email/send', payload);
      toast.success(`Offer ${offerNumber} sent to ${to.trim()}`);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send email';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`send-offer-to-agent-modal-${offerNumber}`}>
        <DialogHeader>
          <DialogTitle>Send Offer {offerNumber} to Agent</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* v3.15-1088 — template selector */}
          <div className="flex items-center justify-between">
            <TemplateSelector
              clientId={client?.id}
              onSelect={({ template, rendered_subject, rendered_body }) => {
                if (!confirmReplaceDraft({ subject, body }, baselineDraft)) return;
                setSubj(rendered_subject || '');
                setBody(rendered_body || '');
                setBaselineDraft({ subject: rendered_subject || '', body: rendered_body || '' });
                setLoadedTemplate(template);
              }}
              testId="offer-to-agent-template"
            />
            {loadedTemplate && (
              <button
                type="button"
                onClick={() => setLoadedTemplate(null)}
                className="text-[11px] text-slate-400 hover:text-slate-600 underline"
                data-testid="offer-to-agent-template-clear"
              >
                Clear template
              </button>
            )}
          </div>
          <TemplateLoadedBanner template={loadedTemplate} testId="offer-to-agent-template-banner" />
          {isAdmin?.() && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setSaveTplOpen(true)}
                className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                data-testid="offer-to-agent-save-as-template"
              >
                Save As Template…
              </button>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              To <span className="text-red-500">*</span>
            </label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="agent@example.com"
              type="email"
              data-testid="send-offer-to-agent-to"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              CC <span className="text-slate-400 font-normal">(comma-separated)</span>
            </label>
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="manager@yourcompany.com"
              data-testid="send-offer-to-agent-cc"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubj(e.target.value)}
              data-testid="send-offer-to-agent-subject"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Body</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="font-sans text-sm"
              data-testid="send-offer-to-agent-body"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="send-offer-to-agent-cancel"
          >Cancel</Button>
          <Button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="send-offer-to-agent-send"
          >
            {sending ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Sending…</> : <><Send className="h-3 w-3 mr-1" /> Send Email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
      <SaveAsTemplateDialog
        open={saveTplOpen}
        onOpenChange={setSaveTplOpen}
        defaultSubject={subject}
        defaultBody={body}
      />
    </Dialog>
  );
}
